import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { TranslocoService } from '@ngneat/transloco';
import { TenantService } from '@core/tenant/tenant.service';
import { ConsentService } from '@core/consent/consent.service';
import { FormService } from '@shared/widgets/form-widget/form.service';
import {
  DealerSiteProfile,
  DealerSite,
  DealerSitePageRef,
  DealerSiteSocialLinks,
  DealerSiteFooter,
} from '@core/tenant/dealer-site.model';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'ade-footer',
  templateUrl: './footer.component.html',
  styleUrls: ['./footer.component.scss'],
})
export class AdeFooterComponent implements OnInit, OnDestroy {
  currentYear = new Date().getFullYear();
  profile: DealerSiteProfile | null = null;
  mapUrl: SafeResourceUrl;

  contactForm: FormGroup;
  sending = false;
  feedback = '';
  feedbackError = false;

  private readonly _fallbackMapUrl =
    'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3003.641399525526!2d-8.608745787486635!3d41.16417560968987!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xd2464574946c66f%3A0xc8d8ac2d4df6bc0a!2sR.%20de%20Faria%20Guimar%C3%A3es%20792%2C%204200-499%20Porto!5e0!3m2!1spt-PT!2spt!4v1775032948303!5m2!1spt-PT!2spt';
  private _unsub = new Subject<void>();

  constructor(
    private _sanitizer: DomSanitizer,
    private _tenantService: TenantService,
    private _transloco: TranslocoService,
    private _consent: ConsentService,
    private _fb: FormBuilder,
    private _formService: FormService,
  ) {
    this.mapUrl = this._sanitizer.bypassSecurityTrustResourceUrl(
      this._fallbackMapUrl,
    );
    this.contactForm = this._fb.group({
      name: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      phone: [''],
      message: ['', Validators.required],
    });
  }

  ngOnInit(): void {
    this._tenantService.profile$
      .pipe(takeUntil(this._unsub))
      .subscribe((profile) => {
        this.profile = profile;
        this.mapUrl = this._sanitizer.bypassSecurityTrustResourceUrl(
          this._mapEmbedUrl(profile?.address),
        );
      });
  }

  /** Envia contacto geral do rodapé para o vendedor (dealer) via /messages. */
  submit(): void {
    if (this.contactForm.invalid) {
      this.contactForm.markAllAsTouched();
      return;
    }
    const receiver = this.profile?.id;
    if (!receiver) {
      this.feedback = 'Não foi possível enviar. Tente mais tarde.';
      this.feedbackError = true;
      return;
    }

    const v = this.contactForm.value;
    this.sending = true;
    this.feedback = '';
    this.feedbackError = false;

    const payload = {
      data: {
        title: `Novo contacto de ${v.name} — ${this.contactName}`,
        message: v.message,
        receiver,
        guest_email: v.email,
        data: {
          url: typeof window !== 'undefined' ? window.location.href : '',
          firstname: v.name,
          lastname: '',
          email: v.email,
          phone: v.phone,
          message: v.message,
        },
      },
    };

    this._formService.sendMessageToVendor(payload).subscribe({
      next: () => {
        this.sending = false;
        this.feedbackError = false;
        this.feedback = 'Mensagem enviada com sucesso!';
        this.contactForm.reset();
      },
      error: () => {
        this.sending = false;
        this.feedbackError = true;
        this.feedback = 'Ocorreu um erro. Tente novamente.';
      },
    });
  }

  ngOnDestroy(): void {
    this._unsub.next();
    this._unsub.complete();
  }

  /** Reabre a barra de consent RGPD. */
  manageCookies(): void {
    this._consent.open();
  }

  get ds(): DealerSite | undefined {
    return this.profile?.dealerSite;
  }

  /** Bloco `socialLinks` (contactos e redes). */
  get sl(): DealerSiteSocialLinks | undefined {
    return this.ds?.socialLinks;
  }

  /** Bloco `footer` (tagline + copyright). */
  get ft(): DealerSiteFooter | undefined {
    return this.ds?.footer;
  }

  get contactName(): string {
    return this.profile?.brandName || this.profile?.name || "Auto D'Época";
  }

  get contactPhone(): string {
    return this.profile?.phone || this.sl?.whatsapp || '+351 912 790 900';
  }

  get contactEmail(): string {
    return this.sl?.contactEmail || 'geral@autodepoca.pt';
  }

  /** Copyright configurável no bloco `footer`; fallback para o texto por omissão. */
  get copyright(): string | null {
    return this.ft?.copyright || null;
  }

  get whatsappUrl(): string | null {
    const w = this.sl?.whatsapp;
    if (!w) return null;
    const v = w.replace(/\s+/g, '');
    return /^https?:/i.test(v)
      ? v
      : `https://wa.me/${v.replace(/[^\d+]/g, '')}`;
  }

  private get _lang(): string {
    return this._transloco.getActiveLang()?.split('-')[0] || 'pt';
  }

  /** Link RGPD: usa a página relacionada no bloco `metaInfo` ou, na sua ausência, uma página do dealer com slug conhecido. */
  get privacyHref(): string | null {
    return this._legalLink(this.ds?.metaInfo?.privacyPolicyPage, [
      'politica-de-privacidade',
      'privacidade',
      'privacy-policy',
    ]);
  }

  get termsHref(): string | null {
    return this._legalLink(this.ds?.metaInfo?.termsPage, [
      'termos-e-condicoes',
      'termos',
      'terms',
    ]);
  }

  private _legalLink(
    page: DealerSitePageRef | undefined,
    slugCandidates: string[],
  ): string | null {
    const slug =
      page?.slug ??
      (this.profile?.pages || []).find((p) => slugCandidates.includes(p.slug))
        ?.slug;
    return slug ? `/${this._lang}/${slug}` : null;
  }

  private _mapEmbedUrl(address: string | null | undefined): string {
    if (!address) return this._fallbackMapUrl;
    return `https://www.google.com/maps?q=${encodeURIComponent(address)}&output=embed`;
  }
}
